import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { FileSpreadsheet, FileDown, ChevronRight, ChevronDown, Target, Check, Loader2, ArrowLeft, MessageSquareText, Milestone, GitMerge, ListChecks, FlaskConical, BarChart3, AlertTriangle, Presentation } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const overallLabels = {
  G: { label: "G - On Track", dot: "bg-green-500", bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300" },
  Y: { label: "Y - At Risk", dot: "bg-yellow-500", bg: "bg-yellow-50 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300" },
  R: { label: "R - Off Track", dot: "bg-red-500", bg: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300" },
  N: { label: "N - Not Started", dot: "bg-slate-400", bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-600 dark:text-slate-300" },
  C: { label: "C - Complete", dot: "bg-blue-500", bg: "bg-blue-50 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300" },
};

const statusColors = {
  "Not Started": "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
  "In Development": "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  "UAT": "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "Delivered": "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "Blocked": "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
  "Cancelled": "bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400",
  "On Hold": "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
  "Discovery": "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
  "Completed": "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  "On Track": "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "In Progress": "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "At Risk": "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "Delayed": "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
  "Off Track": "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
  "Passed": "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "Failed": "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
};

const healthColors = {
  "On Track": { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300", dot: "bg-green-500" },
  "At Risk": { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500" },
  "Off Track": { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300", dot: "bg-red-500" },
  "No Data": { bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-500 dark:text-slate-400", dot: "bg-slate-400" },
};

const reportTypes = [
  { id: "product-goals", title: "Product Goals", desc: "Goals with status, progress and updates", icon: Target, color: "from-teal-400 to-teal-500" },
  { id: "milestones", title: "Milestones Status", desc: "All milestones with status and dates", icon: Milestone, color: "from-blue-400 to-blue-500" },
  { id: "requirements-traceability", title: "Requirements Traceability", desc: "Requirements mapped to pain points and stories", icon: GitMerge, color: "from-violet-400 to-violet-500" },
  { id: "dev-tracking", title: "Development Tracking", desc: "User stories with Jira IDs and progress", icon: ListChecks, color: "from-indigo-400 to-indigo-500" },
  { id: "uat-progress", title: "UAT Progress", desc: "UAT items by product, team and status", icon: FlaskConical, color: "from-amber-400 to-amber-500" },
  { id: "executive-summary", title: "Executive Summary", desc: "Portfolio overview across all products", icon: BarChart3, color: "from-rose-400 to-rose-500" },
  { id: "product-presentation", title: "Product Presentation", desc: "Export full PPTX deck for a product", icon: Presentation, color: "from-purple-400 to-purple-500", isPptx: true },
];

// ─── SHARED PRODUCT FILTER ────────────────────────────────────────────────────
const ProductFilter = ({ products, selectedIds, setSelectedIds }) => {
  const allSelected = selectedIds.length === 0 || selectedIds.length === products.length;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="report-product-filter">
      <Badge className={`cursor-pointer ${allSelected ? "bg-[#0F172A] text-white dark:bg-slate-200 dark:text-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"} hover:opacity-80`} onClick={() => setSelectedIds([])}>All Products</Badge>
      {products.map((p) => (
        <Badge key={p.id} className={`cursor-pointer ${selectedIds.includes(p.id) && !allSelected ? "bg-[#0F172A] text-white dark:bg-slate-200 dark:text-slate-900" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"} hover:opacity-80`}
          onClick={() => { const next = selectedIds.includes(p.id) ? selectedIds.filter((i) => i !== p.id) : [...selectedIds, p.id]; setSelectedIds(next); }}>{p.name}</Badge>
      ))}
    </div>
  );
};

const ReportHeader = ({ title, onBack, onExport, exporting, count }) => (
  <>
    <div className="flex items-center gap-3 mb-4">
      <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white" data-testid="report-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
      <div>
        <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-1">Report</p>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{title}</h2>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">{count} rows</Badge>
        <Button onClick={onExport} disabled={exporting} className="bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-slate-700 dark:hover:bg-slate-600 text-xs h-9 px-4" data-testid="report-export-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />} Export Excel
        </Button>
      </div>
    </div>
    <Separator className="mb-4 bg-slate-200 dark:bg-slate-700" />
  </>
);

// ─── 1. PRODUCT GOALS REPORT ─────────────────────────────────────────────────
const ProductGoalsReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/product-goals`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/product-goals/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "product_goals_report.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const toggleExpand = (idx) => setExpandedIds((p) => ({ ...p, [idx]: !p[idx] }));

  return (
    <div data-testid="product-goals-report">
      <ReportHeader title="Product Goals Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={data.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-[1fr_55px_40px_1fr_110px_80px_80px_70px_90px_90px_90px_80px_40px] gap-0 px-4 py-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 rounded-t-xl">
            <span>Product</span><span className="text-center">Year</span><span className="text-center">Qtr</span><span>Strategic Priority</span>
            <span>Goal Status</span><span className="text-center">Overall</span><span className="text-center">%</span><span>Type</span>
            <span>Team</span><span>Start</span><span>Target End</span><span>Actual End</span><span></span>
          </div>
          {data.map((r, idx) => {
            const os = overallLabels[r.overall_status] || overallLabels.N;
            const isExpanded = !!expandedIds[idx];
            const hasUpdates = r.updates && r.updates.length > 0;
            return (
              <div key={idx} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                <div className={`grid grid-cols-[1fr_55px_40px_1fr_110px_80px_80px_70px_90px_90px_90px_80px_40px] gap-0 px-4 py-3 items-center text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${isExpanded ? "bg-slate-50/80 dark:bg-slate-700/20" : ""}`} onClick={() => hasUpdates && toggleExpand(idx)}>
                  <span className="font-semibold text-slate-800 dark:text-slate-100 truncate pr-2">{r.product_name}</span>
                  <span className="text-center text-slate-600 dark:text-slate-300">{r.year}</span>
                  <span className="text-center text-slate-600 dark:text-slate-300">{r.goal_quarter}</span>
                  <span className="text-slate-600 dark:text-slate-300 truncate pr-2">{r.strategic_priority}</span>
                  <Badge className={`text-[10px] ${statusColors[r.goal_status] || statusColors["Not Started"]}`}>{r.goal_status || "—"}</Badge>
                  <div className="flex items-center justify-center gap-1"><div className={`w-2 h-2 rounded-full ${os.dot}`} /><span className={`text-[10px] font-bold ${os.text}`}>{r.overall_status || "N"}</span></div>
                  <span className="text-center font-mono font-semibold text-slate-700 dark:text-slate-200">{r.percent_complete}%</span>
                  <span className="text-slate-500 dark:text-slate-400 truncate">{r.goal_type}</span>
                  <span className="text-slate-500 dark:text-slate-400 truncate">{r.product_team}</span>
                  <span className="text-slate-500 dark:text-slate-400 text-[11px]">{r.start_date}</span>
                  <span className="text-slate-500 dark:text-slate-400 text-[11px]">{r.target_end_date}</span>
                  <span className="text-slate-500 dark:text-slate-400 text-[11px]">{r.actual_end_date}</span>
                  <span className="flex justify-center">{hasUpdates && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}</span>
                </div>
                {isExpanded && r.updates?.length > 0 && (
                  <div className="px-6 pb-3 space-y-1.5 bg-slate-50/50 dark:bg-slate-800/30">
                    {r.updates.map((u, ui) => (
                      <div key={ui} className="flex items-start gap-2 text-xs"><MessageSquareText className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" /><span className="text-slate-400 font-mono text-[10px] mt-0.5 w-20 flex-shrink-0">{u.date}</span><span className="text-slate-600 dark:text-slate-300">{u.text}</span></div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {data.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No goals found for selected products</div>}
        </Card>
      )}
    </div>
  );
};

// ─── 2. MILESTONES STATUS REPORT ─────────────────────────────────────────────
const MilestonesReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/milestones`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/milestones/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "milestones_report.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const filtered = statusFilter ? data.filter((d) => d.status === statusFilter) : data;
  const statuses = [...new Set(data.map((d) => d.status))];

  return (
    <div data-testid="milestones-report">
      <ReportHeader title="Milestones Status Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={filtered.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge className={`cursor-pointer ${!statusFilter ? "bg-[#0F172A] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`} onClick={() => setStatusFilter("")}>All</Badge>
        {statuses.map((s) => <Badge key={s} className={`cursor-pointer ${statusFilter === s ? "bg-[#0F172A] text-white" : statusColors[s] || "bg-slate-100 text-slate-600"}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}>{s} ({data.filter((d) => d.status === s).length})</Badge>)}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-[1fr_1.5fr_120px_100px_100px_120px_1fr] gap-0 px-4 py-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span>Product</span><span>Milestone</span><span>Status</span><span>Target Date</span><span>Actual Date</span><span>Owner</span><span>Notes</span>
          </div>
          {filtered.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1.5fr_120px_100px_100px_120px_1fr] gap-0 px-4 py-3 items-center text-xs border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/30">
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.product_name}</span>
              <span className="text-slate-700 dark:text-slate-200">{r.title}</span>
              <Badge className={`text-[10px] ${statusColors[r.status] || ""}`}>{r.status}</Badge>
              <span className="text-slate-500 dark:text-slate-400">{r.target_end}</span>
              <span className="text-slate-500 dark:text-slate-400">{r.actual_end || "—"}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.owner || "—"}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.notes || "—"}</span>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No milestones found</div>}
        </Card>
      )}
    </div>
  );
};

// ─── 3. REQUIREMENTS TRACEABILITY REPORT ─────────────────────────────────────
const RequirementsTraceabilityReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showGapsOnly, setShowGapsOnly] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/requirements-traceability`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/requirements-traceability/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "requirements_traceability.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const filtered = showGapsOnly ? data.filter((d) => !d.has_pain_point || !d.has_stories) : data;
  const gapCount = data.filter((d) => !d.has_pain_point || !d.has_stories).length;

  return (
    <div data-testid="req-traceability-report">
      <ReportHeader title="Requirements Traceability Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={filtered.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      <div className="flex items-center gap-3 mb-4">
        <Switch checked={showGapsOnly} onCheckedChange={setShowGapsOnly} /><span className="text-xs text-slate-500 dark:text-slate-400">Show coverage gaps only ({gapCount})</span>
        {gapCount > 0 && <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"><AlertTriangle className="w-3 h-3 mr-1" />{gapCount} gaps</Badge>}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_1.2fr_50px_100px_70px_1fr_100px_120px] gap-0 px-4 py-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span>Product</span><span>REQ ID</span><span>Requirement</span><span>Pri</span><span>Status</span><span>PP</span><span>Pain Point</span><span>Stories</span><span>Story Status</span>
          </div>
          {filtered.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_1.2fr_50px_100px_70px_1fr_100px_120px] gap-0 px-4 py-3 items-center text-xs border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/30">
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.product_name}</span>
              <span className="font-mono font-bold text-violet-600 dark:text-violet-400">{r.req_id}</span>
              <span className="text-slate-700 dark:text-slate-200 truncate">{r.req_title}</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200 text-center">{r.priority}</span>
              <Badge className={`text-[10px] ${statusColors[r.status] || ""}`}>{r.status || "—"}</Badge>
              <span className={`font-mono font-bold text-xs ${r.has_pain_point ? "text-amber-600 dark:text-amber-400" : "text-red-400"}`}>{r.pain_point}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.pain_point_title || "—"}</span>
              <span className={`font-mono font-bold text-xs ${r.has_stories ? "text-violet-600 dark:text-violet-400" : "text-red-400"}`}>{r.linked_stories}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.story_statuses || "—"}</span>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No requirements found</div>}
        </Card>
      )}
    </div>
  );
};

// ─── 4. DEVELOPMENT TRACKING REPORT ──────────────────────────────────────────
const DevTrackingReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState({ rows: [], summary: {} });
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/dev-tracking`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/dev-tracking/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "dev_tracking_report.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const filtered = statusFilter ? data.rows.filter((d) => d.status === statusFilter) : data.rows;

  return (
    <div data-testid="dev-tracking-report">
      <ReportHeader title="Development Tracking Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={filtered.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge className={`cursor-pointer ${!statusFilter ? "bg-[#0F172A] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`} onClick={() => setStatusFilter("")}>All ({data.rows.length})</Badge>
        {Object.entries(data.summary).filter(([, v]) => v > 0).map(([k, v]) => (
          <Badge key={k} className={`cursor-pointer ${statusFilter === k ? "bg-[#0F172A] text-white" : statusColors[k] || "bg-slate-100 text-slate-600"}`} onClick={() => setStatusFilter(statusFilter === k ? "" : k)}>{k} ({v})</Badge>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-[1fr_70px_1.5fr_100px_70px_1fr_120px] gap-0 px-4 py-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span>Product</span><span>Story ID</span><span>User Story</span><span>Jira ID</span><span>REQ ID</span><span>Requirement</span><span>Status</span>
          </div>
          {filtered.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_1.5fr_100px_70px_1fr_120px] gap-0 px-4 py-3 items-center text-xs border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/30">
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.product_name}</span>
              <span className="font-mono font-bold text-violet-600 dark:text-violet-400">{r.story_id}</span>
              <span className="text-slate-700 dark:text-slate-200 truncate">{r.user_story_title}</span>
              <span className="font-mono text-blue-600 dark:text-blue-400">{r.jira_id || "—"}</span>
              <span className="font-mono font-bold text-violet-600 dark:text-violet-400">{r.req_id}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.req_title || "—"}</span>
              <Badge className={`text-[10px] ${statusColors[r.status] || ""}`}>{r.status}</Badge>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No tracking items found</div>}
        </Card>
      )}
    </div>
  );
};

// ─── 5. UAT PROGRESS REPORT ─────────────────────────────────────────────────
const UATProgressReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState({ rows: [], summary: {} });
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/uat-progress`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/uat-progress/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "uat_progress_report.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const filtered = statusFilter ? data.rows.filter((d) => d.status === statusFilter) : data.rows;
  const statuses = [...new Set(data.rows.map((d) => d.status))];

  return (
    <div data-testid="uat-progress-report">
      <ReportHeader title="UAT Progress Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={filtered.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge className={`cursor-pointer ${!statusFilter ? "bg-[#0F172A] text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`} onClick={() => setStatusFilter("")}>All ({data.rows.length})</Badge>
        {statuses.map((s) => <Badge key={s} className={`cursor-pointer ${statusFilter === s ? "bg-[#0F172A] text-white" : statusColors[s] || "bg-slate-100 text-slate-600"}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}>{s} ({data.rows.filter((d) => d.status === s).length})</Badge>)}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card className="border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-[1fr_50px_1.5fr_50px_100px_120px_120px_90px_1fr] gap-0 px-4 py-2.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span>Product</span><span>UAT#</span><span>Title</span><span>Pri</span><span>Status</span><span>Team</span><span>Assigned To</span><span>Due Date</span><span>What to Test</span>
          </div>
          {filtered.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_50px_1.5fr_50px_100px_120px_120px_90px_1fr] gap-0 px-4 py-3 items-center text-xs border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/30">
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.product_name}</span>
              <span className="text-center font-mono text-slate-600 dark:text-slate-300">{r.uat_number}</span>
              <span className="text-slate-700 dark:text-slate-200 truncate">{r.title}</span>
              <span className="text-center font-semibold">{r.priority}</span>
              <Badge className={`text-[10px] ${statusColors[r.status] || ""}`}>{r.status}</Badge>
              <span className="text-teal-700 dark:text-teal-400 font-semibold truncate">{r.team || "—"}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.assigned_to || "—"}</span>
              <span className="text-slate-500 dark:text-slate-400">{r.due_date || "—"}</span>
              <span className="text-slate-500 dark:text-slate-400 truncate">{r.what_testing || "—"}</span>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No UAT items found</div>}
        </Card>
      )}
    </div>
  );
};

// ─── 6. EXECUTIVE SUMMARY REPORT ─────────────────────────────────────────────
const ExecutiveSummaryReport = ({ onBack }) => {
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/executive-summary`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(pRes.data);
      setData(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, selectedIds]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/executive-summary/export`, { params: { product_ids: selectedIds.join(",") || undefined }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "executive_summary.xlsx"; a.click();
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const StatBox = ({ label, value, sub }) => (
    <div className="text-center">
      <div className="text-2xl font-bold text-slate-800 dark:text-slate-100 font-['Outfit']">{value}</div>
      <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-semibold tracking-wider">{label}</div>
      {sub && <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div data-testid="executive-summary-report">
      <ReportHeader title="Executive Summary Report" onBack={onBack} onExport={exportExcel} exporting={exporting} count={data.length} />
      <ProductFilter products={products} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-4">
          {data.map((p) => {
            const h = healthColors[p.health] || healthColors["No Data"];
            return (
              <Card key={p.product_name} className="border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid={`exec-product-${p.product_name}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{p.product_name}</h3>
                    <Badge className={`${h.bg} ${h.text} border text-xs font-semibold`}>
                      <div className={`w-2 h-2 rounded-full ${h.dot} mr-1.5`} />{p.health}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                    <StatBox label="Goals" value={p.goals.total} sub={`${p.goals.avg_complete}% avg`} />
                    <StatBox label="Milestones" value={p.milestones.total} sub={`${p.milestones.statuses?.Completed || 0} done`} />
                    <StatBox label="Requirements" value={p.requirements.total} sub={`${p.requirements.with_stories} w/ stories`} />
                    <StatBox label="Stories" value={p.stories.total} sub={`${p.stories.statuses?.Delivered || 0} delivered`} />
                    <StatBox label="UAT Items" value={p.uat.total} sub={`${p.uat.statuses?.Passed || 0} passed`} />
                    <div className="text-center">
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {Object.entries(p.goals.statuses || {}).map(([k, v]) => {
                          const os = overallLabels[k];
                          return os ? <span key={k} className="flex items-center gap-0.5 text-[10px]"><span className={`w-1.5 h-1.5 rounded-full ${os.dot}`} /><span className={`${os.text} font-semibold`}>{v}</span></span> : null;
                        })}
                      </div>
                      <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-semibold tracking-wider mt-1">Goal Status</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {data.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No products found</div>}
        </div>
      )}
    </div>
  );
};

// ─── PRODUCT PRESENTATION REPORT ──────────────────────────────────────────────
const ProductPresentationReport = ({ onBack }) => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProducts(res.data || []))
      .catch(() => {});
  }, [token]);

  const handleExport = async () => {
    if (!selectedProduct) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/product-presentation/export?product_id=${selectedProduct}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const prod = products.find(p => p.id === selectedProduct);
      link.download = `${(prod?.name || "Product").replace(/[^a-zA-Z0-9_ -]/g, "").replace(/ /g, "_")}_Presentation.pptx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Export failed");
    }
    setExporting(false);
  };

  const slides = [
    { num: 1, title: "Title Slide", desc: "Product name, status" },
    { num: 2, title: "Overview", desc: "Project goal / description" },
    { num: 3, title: "Stakeholders", desc: "Business & IT stakeholders" },
    { num: 4, title: "Pain Points", desc: "All pain points with status" },
    { num: 5, title: "Requirements", desc: "Requirements with priority & status" },
    { num: 6, title: "Development Tracking", desc: "User stories, Jira IDs, linked reqs" },
    { num: 7, title: "Milestones", desc: "Milestones with dates & status" },
    { num: 8, title: "As-Is Experience", desc: "Process flow diagram" },
    { num: 9, title: "To-Be Experience", desc: "Target process flow diagram" },
    { num: 10, title: "To-Be Field Mapping", desc: "Field mapping table" },
    { num: 11, title: "UAT", desc: "UAT items with team & tester" },
    { num: 12, title: "Go / No-Go", desc: "Stakeholder votes & comments" },
  ];

  return (
    <div data-testid="product-presentation-report">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 mb-4 transition-colors" data-testid="report-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-purple-500 mb-1">Export</p>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">Product Presentation</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Generate a comprehensive PPTX deck covering all product panels</p>
        </div>
      </div>

      <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1 w-full sm:max-w-xs">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Select Product</label>
              <select
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                data-testid="presentation-product-select"
              >
                <option value="">Choose a product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleExport}
              disabled={!selectedProduct || exporting}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2 px-5"
              data-testid="export-pptx-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" />}
              {exporting ? "Generating..." : "Export PPTX"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 font-['Outfit']">Slides Included ({slides.length})</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {slides.map((s) => (
            <div key={s.num} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30" data-testid={`slide-preview-${s.num}`}>
              <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-bold">{s.num}</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{s.title}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-tight mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN REPORTS PAGE ────────────────────────────────────────────────────────
export const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState(null);

  const reportComponents = {
    "product-goals": ProductGoalsReport,
    milestones: MilestonesReport,
    "requirements-traceability": RequirementsTraceabilityReport,
    "dev-tracking": DevTrackingReport,
    "uat-progress": UATProgressReport,
    "executive-summary": ExecutiveSummaryReport,
    "product-presentation": ProductPresentationReport,
  };

  if (activeReport && reportComponents[activeReport]) {
    const ReportComponent = reportComponents[activeReport];
    return <ReportComponent onBack={() => setActiveReport(null)} />;
  }

  return (
    <div data-testid="reports-page">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Analytics</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Reports</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Generate and export consolidated reports</p>
      <Separator className="my-6 bg-slate-200 dark:bg-slate-700" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="report-type-grid">
        {reportTypes.map((r) => (
          <Card key={r.id} className="group cursor-pointer border border-slate-200 dark:border-slate-700 hover:border-[#00D4C8] dark:hover:border-[#00D4C8] hover:shadow-lg transition-all duration-200 bg-white dark:bg-slate-800/50" onClick={() => setActiveReport(r.id)} data-testid={`report-card-${r.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${r.color} shadow-sm`}><r.icon className="w-5 h-5 text-white" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 group-hover:text-[#00D4C8] transition-colors font-['Outfit']">{r.title}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{r.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-[#00D4C8] transition-colors mt-1 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
